import { AdvancedChartSettings } from './AdvancedChart';
import { ResponseError } from './Types';
import axios from 'axios';

type Environments = 'qa'|'stg'|'uat'|'prod';

export interface MorningstarComponentsConfig {
	[key: string]: any
	'markets-components-svgchart'?: AdvancedChartSettings
}

export interface MorningstarComponentLoaderSettings
{
	apiTokens: {
		/**
		 * Authentication key needed by the component
		 */
		apiGatewayToken: string
	}
	configuration: {
		/**
		 * Reference to your firm's specific configuration for this component, hosted by Morningstar services
		 */
		namespace: string
		/**
		 * Define the environment
		 */
		environment: Environments
	}
	componentSource: {
		/**
		 * Institution Id
		 */
		instid?: string
		/**
		 * Decides if the component manifest is taken from the config api or a CDN (qs, mwc)
		 * default: 'mwc'
		 */
		type?: 'resolver'|'qs'|'mwc'
		/**
		 * Defines which environment the bundles are taken from for CDNs or which version of the resolver should be used for the resolver
		 * default: 'prod'
		 */
		environment?: Environments
	}
	angular?: {
		/**
		 * Whether the loader should call angular bootstrap or not
		 * default: true
		 */
		bootstrap?: boolean
		/**
		 * Bind the container and the componentId for a component
		 * default: []
		 */
		elementBindings?: [
			{
				morningstarComponentId: string
				container: string
			}
		]
		/**
		 * Either the bootstrap element or a function used to find the angular bootstrap element
		 * default: null
		 */
		bootstrapElement?: any
	}
	css?: {
		/**
		 * Style overrides by specifying the CSS file paths
		 * default: void
		 */
		customStyles?: string[]
		/**
		 * Whether to include default styles
		 * default: true
		 */
		includeComponentCss?: boolean
	}
}

export interface MorningstarComponentLoaderTokenResponse
{
	apiGatewayToken?: string
	errors?: ResponseError[]
}

interface ErrorHandler {
	(error: any): void
}

export default class MorningstarComponentLoader
{
	protected settings: MorningstarComponentLoaderSettings;
	
	constructor(settings: MorningstarComponentLoaderSettings)
	{
		this.settings = settings;
	};
	
	/**
	 * Initialise a Morningstar component
	 * 
	 * @param window 
	 * @param containerId
	 * @param componentConfig 
	 */
	initComponent (window: Window, containerId: string, componentConfig: MorningstarComponentsConfig, errorHandler: ErrorHandler = null): void
	{
		const _window: any = window as any;
		const componentKey = Object.keys(componentConfig)[0];
		const componentSettings = (componentConfig as any)[componentKey];

		const initComponent = (containerId: string) =>
		{
			return () => {
				_window.morningstar.initComponent(componentKey, {
					container: document.getElementById(containerId)
				});
			};
		};
		// check morningstar has been loaded on the window
		if ( _window.morningstar !== undefined ) {
			// alter the loader settings accordingly (is this bad practice.?)
			let _loaderSettings = this.settings as any;
			// add specific component settings
			_loaderSettings.configuration['overrides'] = {
				components: {}
			};
			_loaderSettings.configuration.overrides.components[componentKey] = {
				settings: componentSettings
			}
			// add static properties
			_loaderSettings.configuration['source'] = {
				type: 'service',
				region: 'emea'
			};
			// load and initialise the component
			_window.morningstar.loader
				.load(_loaderSettings)
				.then(initComponent(containerId))
				.catch ((error: any) => {
					// an error occurred, if an error handler
					// is defined pass the error along
					if ( errorHandler !== null ) {
						errorHandler(error);
					}
				});
		}
	};

	getConfigEnvironment = (): Environments =>
	{
		return this.settings.configuration.environment;
	};

	/**
	 * TODO: This should be improved by using a state container (Redux) to store the token and expiry time
	 * so that if another request arrives for a token while there is still one active it should not create another
	 * but return the exiting valid token
	 * 
	 * @param url 
	 * @param username 
	 * @param password 
	 */
	static async getMorningstarComponentLoaderToken(url: string, username: string, password: string): Promise<MorningstarComponentLoaderTokenResponse>
	{
		let response: MorningstarComponentLoaderTokenResponse = {};
		let postResult: any;
		// make request and fetch the result or handle error
		try {
			const authHeaderValue = Buffer.from(`${username}:${password}`, 'utf-8').toString('base64');
			postResult = await axios.post(url, {}, {
				headers: {
					Authorization: `Basic ${authHeaderValue}`
				}
			});
			response['apiGatewayToken'] = postResult.data.access_token;
		}
		catch (e) {
			// an error occurred while making the request
			const error: ResponseError = {
				message: e.response.statusText,
				extensions: {
					code: e.response.status
				}
			};
			response.errors = [error];
		}
		return response;
	};
}